import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  MainProcessConfig,
  RecoveredHeatUnit,
  RecoveredWaterUnit
} from "../../../../../types/process/process.types";
import { Card, CardContent, CardHeader } from "../../../../../components/ui/common/card";
import { Check, ChevronDown, ChevronUp, Droplets, Flame, Pencil, RefreshCw } from "lucide-react";
import { Badge } from "../../../../../components/ui/common/badge";
import { Button } from "../../../../../components/ui/common/button";
import { Form } from "../../../../../components/ui/common/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "../../../../../components/ui/common/tooltip";
import { FormUnitInput } from "../../../../../components/ui/Input/FormUnitInput/FormUnitInput";

const RECOVERED_WATER_UNITS: RecoveredWaterUnit[] = ["L", "m³"];
const RECOVERED_HEAT_UNITS: RecoveredHeatUnit[] = ["MJ/kg", "MJ/ton"];
const RECOVERED_WATER_UNIT_OPTIONS = RECOVERED_WATER_UNITS.map(u => ({ value: u, label: u }));
const RECOVERED_HEAT_UNIT_OPTIONS = RECOVERED_HEAT_UNITS.map(u => ({ value: u, label: u }));

interface RecoveryAmountFormValues {
  amount: string;
  unit: string;
}

interface Props {
  config: MainProcessConfig;
  onUpdate: (patch: Partial<MainProcessConfig>) => void;
  disabled: boolean;
}

const RecoverySection = ({ config, onUpdate, disabled }: Props) => {
  const [open, setOpen] = useState(false);
  const [editingWater, setEditingWater] = useState(false);
  const [editingHeat, setEditingHeat] = useState(false);

  const waterForm = useForm<RecoveryAmountFormValues>({
    defaultValues: {
      amount: config.recoveredWaterAmount === "" ? "" : String(config.recoveredWaterAmount),
      unit: config.recoveredWaterUnit
    }
  });
  const heatForm = useForm<RecoveryAmountFormValues>({
    defaultValues: {
      amount: config.recoveredHeatAmount === "" ? "" : String(config.recoveredHeatAmount),
      unit: config.recoveredHeatUnit
    }
  });

  useEffect(() => {
    const subscription = waterForm.watch(data => {
      onUpdate({
        recoveredWaterAmount: data.amount === "" ? "" : Math.max(0, Number(data.amount)),
        recoveredWaterUnit: data.unit as RecoveredWaterUnit
      });
    });
    return () => subscription.unsubscribe();
  }, [waterForm, onUpdate]);

  useEffect(() => {
    const subscription = heatForm.watch(data => {
      onUpdate({
        recoveredHeatAmount: data.amount === "" ? "" : Math.max(0, Number(data.amount)),
        recoveredHeatUnit: data.unit as RecoveredHeatUnit
      });
    });
    return () => subscription.unsubscribe();
  }, [heatForm, onUpdate]);

  const hasWaterRecovery =
    config.isRecoveringWater &&
    typeof config.recoveredWaterAmount === "number" &&
    config.recoveredWaterAmount > 0;
  const hasHeatRecovery =
    config.isRecoveringHeat &&
    typeof config.recoveredHeatAmount === "number" &&
    config.recoveredHeatAmount > 0;
  const recoveryCount = (hasWaterRecovery ? 1 : 0) + (hasHeatRecovery ? 1 : 0);

  return (
    <Card className="tw-border-border !tw-py-0">
      <CardHeader
        className="tw-bg-secondary/50 tw-py-3 tw-px-5 tw-rounded-t-lg tw-cursor-pointer"
        onClick={() => setOpen(v => !v)}
      >
        <div className="tw-flex tw-items-center tw-justify-between">
          <div className="tw-flex tw-items-center tw-gap-2">
            <RefreshCw className="tw-h-4 tw-w-4 tw-text-primary" />
            <span className="tw-text-sm tw-font-semibold tw-text-foreground">Recovery</span>
            {recoveryCount > 0 && (
              <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
                {recoveryCount} active
              </Badge>
            )}
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            {!open && (hasWaterRecovery || hasHeatRecovery) && (
              <span className="tw-text-[10px] tw-text-muted-foreground tw-font-mono">
                {hasWaterRecovery &&
                  `Water: ${config.recoveredWaterAmount} ${config.recoveredWaterUnit}`}
                {hasWaterRecovery && hasHeatRecovery && " · "}
                {hasHeatRecovery &&
                  `Heat: ${config.recoveredHeatAmount} ${config.recoveredHeatUnit}`}
              </span>
            )}
            {open ? (
              <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            ) : (
              <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
            )}
          </div>
        </div>
      </CardHeader>
      {open && (
        <CardContent
          className={`tw-py-4 tw-px-5 tw-space-y-4 ${
            disabled ? "tw-opacity-50 tw-pointer-events-none" : ""
          }`}
        >
          <p className="tw-text-xs tw-text-muted-foreground">
            Are you recovering water or heat in this processing step?
          </p>

          {/* Water Recovery */}
          <div className="tw-border tw-border-dashed tw-border-primary/40 tw-rounded-md tw-p-3 tw-space-y-3">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Droplets className="tw-h-3.5 tw-w-3.5 tw-text-primary" />
              <span className="tw-text-xs tw-font-medium tw-text-foreground">Water Recovery</span>
            </div>

            <div className="tw-flex tw-gap-2">
              <Button
                variant={config.isRecoveringWater ? "default" : "outline"}
                size="sm"
                className="tw-text-xs tw-h-7 tw-px-4"
                onClick={() => onUpdate({ isRecoveringWater: true })}
              >
                Yes
              </Button>
              <Button
                variant={!config.isRecoveringWater ? "default" : "outline"}
                size="sm"
                className="tw-text-xs tw-h-7 tw-px-4"
                onClick={() => {
                  onUpdate({ isRecoveringWater: false, recoveredWaterAmount: "" });
                  waterForm.reset({ amount: "", unit: config.recoveredWaterUnit });
                  setEditingWater(false);
                }}
              >
                No
              </Button>
            </div>

            {config.isRecoveringWater && (
              <>
                {/* Saved state - compact view */}
                {hasWaterRecovery && !editingWater ? (
                  <div className="tw-flex tw-items-center tw-gap-2 tw-py-1.5 tw-px-2 tw-rounded-md tw-bg-muted/30 tw-border tw-border-border/50">
                    <div className="tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-text-xs">
                      <span className="tw-font-medium tw-text-foreground">Recovered Water</span>
                      <span className="tw-font-mono tw-text-muted-foreground tw-ml-auto">
                        {config.recoveredWaterAmount} {config.recoveredWaterUnit}
                      </span>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="tw-h-7 tw-w-7 tw-text-muted-foreground hover:tw-text-foreground"
                            onClick={() => setEditingWater(true)}
                          >
                            <Pencil className="tw-h-3 tw-w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="tw-text-xs">Edit</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                ) : (
                  /* Edit / input state */
                  <div
                    className={`tw-flex tw-items-center tw-gap-2 ${
                      editingWater
                        ? "tw-border tw-border-primary/30 tw-rounded-md tw-p-2 tw-bg-primary/5"
                        : ""
                    }`}
                  >
                    <div className="tw-w-48 [&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
                      <Form {...waterForm}>
                        <FormUnitInput
                          control={waterForm.control}
                          name="amount"
                          label=""
                          placeholder="Amount"
                          enableLimitedDecimal
                          unitName="unit"
                          unitOptions={RECOVERED_WATER_UNIT_OPTIONS}
                        />
                      </Form>
                    </div>
                    {editingWater ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="default"
                              size="icon"
                              className="tw-h-10 tw-w-10"
                              onClick={() => setEditingWater(false)}
                            >
                              <Check className="tw-h-3.5 tw-w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="tw-text-xs">Save changes</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="default"
                              size="icon"
                              className="tw-h-10 tw-w-10"
                              disabled={
                                config.recoveredWaterAmount === "" ||
                                config.recoveredWaterAmount === 0
                              }
                              onClick={() => setEditingWater(false)}
                            >
                              <Check className="tw-h-3.5 tw-w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="tw-text-xs">Save</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Heat Recovery */}
          <div className="tw-border tw-border-dashed tw-border-accent/50 tw-rounded-md tw-p-3 tw-space-y-3">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Flame className="tw-h-3.5 tw-w-3.5 tw-text-accent" />
              <span className="tw-text-xs tw-font-medium tw-text-foreground">Heat Recovery</span>
            </div>

            <div className="tw-flex tw-gap-2">
              <Button
                variant={config.isRecoveringHeat ? "default" : "outline"}
                size="sm"
                className="tw-text-xs tw-h-7 tw-px-4"
                onClick={() => onUpdate({ isRecoveringHeat: true })}
              >
                Yes
              </Button>
              <Button
                variant={!config.isRecoveringHeat ? "default" : "outline"}
                size="sm"
                className="tw-text-xs tw-h-7 tw-px-4"
                onClick={() => {
                  onUpdate({ isRecoveringHeat: false, recoveredHeatAmount: "" });
                  heatForm.reset({ amount: "", unit: config.recoveredHeatUnit });
                  setEditingHeat(false);
                }}
              >
                No
              </Button>
            </div>

            {config.isRecoveringHeat && (
              <>
                {hasHeatRecovery && !editingHeat ? (
                  <div className="tw-flex tw-items-center tw-gap-2 tw-py-1.5 tw-px-2 tw-rounded-md tw-bg-muted/30 tw-border tw-border-border/50">
                    <div className="tw-flex-1 tw-flex tw-items-center tw-gap-3 tw-text-xs">
                      <span className="tw-font-medium tw-text-foreground">Recovered Heat</span>
                      <span className="tw-font-mono tw-text-muted-foreground tw-ml-auto">
                        {config.recoveredHeatAmount} {config.recoveredHeatUnit}
                      </span>
                    </div>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="tw-h-7 tw-w-7 tw-text-muted-foreground hover:tw-text-foreground"
                            onClick={() => setEditingHeat(true)}
                          >
                            <Pencil className="tw-h-3 tw-w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="tw-text-xs">Edit</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                ) : (
                  <div
                    className={`tw-flex tw-items-center tw-gap-2 ${
                      editingHeat
                        ? "tw-border tw-border-primary/30 tw-rounded-md tw-p-2 tw-bg-primary/5"
                        : ""
                    }`}
                  >
                    <div className="tw-w-48 [&_label]:tw-hidden [&_.tw-space-y-2]:tw-space-y-0">
                      <Form {...heatForm}>
                        <FormUnitInput
                          control={heatForm.control}
                          name="amount"
                          label=""
                          placeholder="Amount"
                          enableLimitedDecimal
                          unitName="unit"
                          unitOptions={RECOVERED_HEAT_UNIT_OPTIONS}
                        />
                      </Form>
                    </div>
                    {editingHeat ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="default"
                              size="icon"
                              className="tw-h-10 tw-w-10"
                              onClick={() => setEditingHeat(false)}
                            >
                              <Check className="tw-h-3.5 tw-w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="tw-text-xs">Save changes</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="default"
                              size="icon"
                              className="tw-h-10 tw-w-10"
                              disabled={
                                config.recoveredHeatAmount === "" ||
                                config.recoveredHeatAmount === 0
                              }
                              onClick={() => setEditingHeat(false)}
                            >
                              <Check className="tw-h-3.5 tw-w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="tw-text-xs">Save</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default RecoverySection;
